/** Labels, categories, and filters for campaign activity events. */

export const ACTIVITY_CATEGORY_OPTIONS = [
  { value: "all", label: "All categories" },
  { value: "campaign", label: "Campaign lifecycle" },
  { value: "enrollment", label: "Prospects & leads" },
  { value: "outreach", label: "Outreach steps" },
  { value: "setup", label: "Setup & config" },
];

export const ACTIVITY_DATE_OPTIONS = [
  { value: "all", label: "All time" },
  { value: "today", label: "Today" },
  { value: "7d", label: "Last 7 days" },
  { value: "30d", label: "Last 30 days" },
];

/** Calendar-day span per range; today counts as day 1 so "7d" covers today plus six days. */
const ACTIVITY_RANGE_DAYS = { today: 1, "7d": 7, "30d": 30 };

/** Always listed in the event-type filter with readable labels. */
export const ACTIVITY_PRIMARY_EVENT_TYPES = [
  "campaign_started",
  "campaign_resumed",
  "campaign_activated",
  "campaign_paused",
  "wait_scheduled",
  "enrollment_exited",
];

const EVENT_LABELS = {
  campaign_started: "Campaign started",
  campaign_paused: "Campaign paused",
  campaign_resumed: "Campaign resumed",
  campaign_activated: "Campaign activated",
  campaign_cancelled: "Campaign cancelled",
  campaign_archived: "Campaign archived",
  campaign_created: "Campaign created",
  campaign_updated: "Campaign updated",
  campaign_deleted: "Campaign deleted",
  campaign_duplicated: "Campaign duplicated",
  workflow_published: "Workflow published",
  enrollment_exited: "Prospect exited",
  enrollment_completed: "Prospect completed sequence",
  lead_status_changed: "Lead status updated",
  reply_received: "Reply received",
  wait_scheduled: "Wait step scheduled",
  message_sent: "Message sent",
  message_queued: "Message queued",
  step_skipped: "Step skipped",
  step_failed: "Step failed",
  channels_assigned: "Channels assigned",
  ai_sequence_designed: "AI sequence designed",
  campaign_default_set: "Set as default campaign",
  campaign_default_cleared: "Default campaign cleared",
  lead_owner_notified: "Lead owner notified",
};

const EVENT_CATEGORIES = {
  campaign: new Set([
    "campaign_started",
    "campaign_paused",
    "campaign_resumed",
    "campaign_activated",
    "campaign_cancelled",
    "campaign_archived",
  ]),
  enrollment: new Set([
    "enrollment_exited",
    "enrollment_completed",
    "lead_status_changed",
    "reply_received",
    "lead_owner_notified",
  ]),
  outreach: new Set([
    "message_sent",
    "message_queued",
    "wait_scheduled",
    "step_skipped",
    "step_failed",
  ]),
  setup: new Set([
    "campaign_created",
    "campaign_updated",
    "campaign_deleted",
    "campaign_duplicated",
    "workflow_published",
    "channels_assigned",
    "ai_sequence_designed",
    "campaign_default_set",
    "campaign_default_cleared",
  ]),
};

const CATEGORY_META = {
  campaign: { label: "Lifecycle", className: "bg-indigo-50 text-indigo-700 ring-indigo-100" },
  enrollment: { label: "Prospect", className: "bg-emerald-50 text-emerald-700 ring-emerald-100" },
  lead: { label: "Lead", className: "bg-amber-50 text-amber-800 ring-amber-100" },
  outreach: { label: "Outreach", className: "bg-sky-50 text-sky-700 ring-sky-100" },
  setup: { label: "Setup", className: "bg-slate-100 text-slate-600 ring-slate-200" },
  other: { label: "Event", className: "bg-slate-100 text-slate-600 ring-slate-200" },
};

/** Badge prefix can be finer-grained than the filter category (e.g. Lead vs Prospect). */
const EVENT_BADGE_KEYS = {
  lead_status_changed: "lead",
  lead_owner_notified: "lead",
};

const LEAD_STATUS_LABELS = {
  new: "New",
  in_progress: "In progress",
  qualified_inquiry: "Qualified (inquiry)",
  qualified_mature: "Qualified (mature)",
  unqualified_inquiry: "Unqualified (inquiry)",
  unqualified_mature: "Unqualified (mature)",
};

const CHANNEL_LABELS = {
  email: "Email",
  linkedin: "LinkedIn",
  whatsapp: "WhatsApp",
  sms: "SMS",
  telegram: "Telegram",
  voice: "AI voice",
};

function humanizeToken(value) {
  if (!value) return "";
  return String(value)
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function leadStatusLabel(value) {
  if (!value) return "Unknown";
  return LEAD_STATUS_LABELS[value] || humanizeToken(value);
}

function channelLabel(value) {
  if (!value) return "";
  return CHANNEL_LABELS[value] || humanizeToken(value);
}

export function getActivityEventCategory(eventType) {
  const key = String(eventType || "");
  for (const [category, types] of Object.entries(EVENT_CATEGORIES)) {
    if (types.has(key)) return category;
  }
  return "other";
}

export function getActivityCategoryMeta(eventType) {
  const key = String(eventType || "");
  const badgeKey = EVENT_BADGE_KEYS[key] || getActivityEventCategory(key);
  return CATEGORY_META[badgeKey] || CATEGORY_META.other;
}

export function formatActivityEventLabel(eventType) {
  const key = String(eventType || "");
  return EVENT_LABELS[key] || humanizeToken(key);
}

function formatTimestamp(value) {
  if (!value) return null;
  try {
    return new Date(value).toLocaleString(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return String(value);
  }
}

// ─── Per-event-type description helpers ───────────────────────────────────────

function descCampaignStarted(payload) {
  if (payload.scheduled_start_at) return `Scheduled to start ${formatTimestamp(payload.scheduled_start_at)}`;
  if (payload.enrollments_activated != null) return `${payload.enrollments_activated} prospect(s) activated`;
  return "Campaign execution started";
}

function descCampaignPaused(payload) {
  return payload.paused != null
    ? `${payload.paused} active prospect(s) paused`
    : "Campaign paused for all active prospects";
}

function descCampaignResumed(payload) {
  return payload.resumed != null
    ? `${payload.resumed} prospect(s) resumed`
    : "Campaign resumed for paused prospects";
}

function descCampaignActivated(payload) {
  return payload.enrolled != null
    ? `Campaign activated · ${payload.enrolled} prospect(s) enrolled`
    : "Campaign activated";
}

function descCampaignCancelled(payload) {
  return payload.enrollments_cancelled != null
    ? `${payload.enrollments_cancelled} prospect(s) cancelled`
    : "Campaign cancelled";
}

function descWorkflowPublished(payload) {
  if (payload.version) return `Published workflow ${payload.version}`;
  if (Array.isArray(payload.warnings) && payload.warnings.length) {
    return `Published with ${payload.warnings.length} warning(s)`;
  }
  return "Workflow published to active prospects";
}

function descCampaignUpdated(payload) {
  if (payload.action === "save") return "Campaign wizard progress saved";
  if (Array.isArray(payload.fields) && payload.fields.length) return `Updated ${payload.fields.join(", ")}`;
  if (payload.step != null) return `Wizard step ${payload.step} saved`;
  return "Campaign settings updated";
}

function descChannelsAssigned(payload) {
  if (Array.isArray(payload.channels) && payload.channels.length) {
    return `Assigned ${payload.channels.map(channelLabel).join(", ")}`;
  }
  return "Outreach channels assigned";
}

function descLeadStatusChanged(payload) {
  let suffix = "";
  if (payload.qualification_source) {
    suffix = ` · ${payload.qualification_source}`;
  } else if (payload.reason) {
    suffix = ` · ${humanizeToken(payload.reason)}`;
  }
  return `${leadStatusLabel(payload.from)} → ${leadStatusLabel(payload.to)}${suffix}`;
}

function descReplyReceived(payload) {
  if (!payload.channel) {
    return "Inbound reply received";
  }
  const channel = channelLabel(payload.channel);
  if (payload.from) {
    return `Reply on ${channel} from ${payload.from}`;
  }
  return `Reply on ${channel}`;
}

function descMessageQueued(payload) {
  if (!payload.channel) {
    return "Outreach message queued";
  }
  let message = `${channelLabel(payload.channel)} message queued`;
  if (payload.recipient) {
    message += ` for ${payload.recipient}`;
  }
  return message;
}

function descMessageSent(payload) {
  if (!payload.channel) {
    return "Outreach message delivered";
  }
  let message = `${channelLabel(payload.channel)} sent`;
  if (payload.recipient) {
    message += ` to ${payload.recipient}`;
  }
  return message;
}

function descCampaignCreated(payload) {
  if (payload.name) return `Created "${payload.name}"`;
  return "New campaign created";
}

function descCampaignDuplicated(payload) {
  if (payload.source_id) return `Duplicated from campaign ${String(payload.source_id).slice(0, 8)}…`;
  return "Campaign duplicated";
}

function descEnrollmentExited(payload) {
  const name = payload.display_name || "Prospect";
  const reason = humanizeToken(payload.reason || "manual exit");
  return `${name} exited · ${reason}`;
}

function descEnrollmentCompleted(payload) {
  if (payload.reason) return `Sequence completed · ${humanizeToken(payload.reason)}`;
  return "Prospect finished the sequence";
}

function descWaitScheduled(payload) {
  if (payload.until) return `Waiting until ${formatTimestamp(payload.until)}`;
  return "Wait step scheduled";
}

const EVENT_DESC_MAP = {
  campaign_started:         (p) => descCampaignStarted(p),
  campaign_paused:          (p) => descCampaignPaused(p),
  campaign_resumed:         (p) => descCampaignResumed(p),
  campaign_activated:       (p) => descCampaignActivated(p),
  campaign_cancelled:       (p) => descCampaignCancelled(p),
  campaign_archived:        ()  => "Campaign archived · existing prospects continue",
  workflow_published:       (p) => descWorkflowPublished(p),
  campaign_updated:         (p) => descCampaignUpdated(p),
  campaign_created:         (p) => descCampaignCreated(p),
  campaign_duplicated:      (p) => descCampaignDuplicated(p),
  campaign_deleted:         ()  => "Campaign removed",
  channels_assigned:        (p) => descChannelsAssigned(p),
  ai_sequence_designed:     (p) => p.summary || "AI-generated sequence applied",
  enrollment_exited:        (p) => descEnrollmentExited(p),
  enrollment_completed:     (p) => descEnrollmentCompleted(p),
  lead_status_changed:      (p) => descLeadStatusChanged(p),
  reply_received:           (p) => descReplyReceived(p),
  wait_scheduled:           (p) => descWaitScheduled(p),
  message_queued:           (p) => descMessageQueued(p),
  message_sent:             (p) => descMessageSent(p),
  step_skipped:             (p) => p.reason ? `Step skipped · ${humanizeToken(p.reason)}` : "Workflow step skipped",
  step_failed:              (p) => (p.error || p.reason) ? `Step failed · ${p.error || humanizeToken(p.reason)}` : "Workflow step failed",
  lead_owner_notified:      (p) => p.lead_status ? `Owner notified · ${leadStatusLabel(p.lead_status)}` : "Lead owner notified",
  campaign_default_set:     ()  => "Marked as default campaign for assigned channels",
  campaign_default_cleared: ()  => "Default campaign assignment cleared",
};

export function formatActivityEventDescription(event) {
  const type = event?.event_type;
  const payload = event?.payload && typeof event.payload === "object" ? event.payload : {};
  const handler = EVENT_DESC_MAP[type];
  return handler ? handler(payload) : null;
}

function startOfLocalDay(date) {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
}

/** Start of the selected range in the viewer's timezone, or null for "all time". */
export function activityRangeStart(dateRange, now = new Date()) {
  const days = ACTIVITY_RANGE_DAYS[dateRange];
  if (!days) return null;
  const start = startOfLocalDay(now);
  start.setDate(start.getDate() - (days - 1));
  return start;
}

/** Cutoff sent to the API so the range applies to every stored event, not just the fetched page. */
export function activityRangeSince(dateRange, now = new Date()) {
  const start = activityRangeStart(dateRange, now);
  return start ? start.toISOString() : null;
}

function matchesDateFilter(createdAt, dateFilter) {
  const start = activityRangeStart(dateFilter);
  if (!start) return true;
  if (!createdAt) return false;
  const eventDate = new Date(createdAt);
  if (Number.isNaN(eventDate.getTime())) return false;
  return eventDate >= start;
}

export function buildActivityEventTypeOptions(events) {
  const fromData = (events || []).map((ev) => ev.event_type).filter(Boolean);
  const merged = [
    ...ACTIVITY_PRIMARY_EVENT_TYPES,
    ...fromData.filter((type) => !ACTIVITY_PRIMARY_EVENT_TYPES.includes(type)),
  ];
  const unique = [...new Set(merged)];
  return [
    { value: "all", label: "All event types" },
    ...unique.map((type) => ({
      value: type,
      label: formatActivityEventLabel(type),
    })),
  ];
}

export function filterCampaignActivity(events, {
  category = "all",
  eventType = "all",
  dateRange = "all",
  enrollmentOnly = false,
} = {}) {
  return (events || []).filter(
    (ev) =>
      (!enrollmentOnly || Boolean(ev.enrollment_id)) &&
      (category === "all" || getActivityEventCategory(ev.event_type) === category) &&
      (eventType === "all" || ev.event_type === eventType) &&
      matchesDateFilter(ev.created_at, dateRange)
  );
}

export function groupActivityByDate(events) {
  const groups = [];
  const indexByKey = new Map();

  for (const ev of events || []) {
    const date = ev.created_at ? new Date(ev.created_at) : null;
    const key = date && !Number.isNaN(date.getTime())
      ? date.toLocaleDateString(undefined, {
        weekday: "long",
        month: "short",
        day: "numeric",
        year: "numeric",
      })
      : "Unknown date";

    if (!indexByKey.has(key)) {
      indexByKey.set(key, groups.length);
      groups.push({ key, events: [] });
    }
    groups[indexByKey.get(key)].events.push(ev);
  }

  return groups;
}
